import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { httpsCallable } from 'firebase/functions';
import { functions } from '@/lib/firebase';
import { Card, CardHeader, CardTitle, CardContent, Button, Input } from '@/components/ui';
import { LanguageSwitcher } from '@/components/common/LanguageSwitcher';
import { Mail, ArrowLeft, Loader2, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';

type RequestState = 'idle' | 'loading' | 'success' | 'error';

export default function ForgotPassword() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [state, setState] = useState<RequestState>('idle');
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrorMessage('');

    // Client-side validation
    if (!email || !email.trim()) {
      toast.error(t('toasts.forgotPassword.enterEmail'));
      return;
    }

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) {
      toast.error(t('toasts.forgotPassword.enterValidEmail'));
      return;
    }

    setState('loading');

    try {
      // Call Cloud Function
      const sendPasswordRecoveryEmail = httpsCallable(functions, 'sendPasswordRecoveryEmail');
      const result = await sendPasswordRecoveryEmail({ email: email.trim() });

      const data = result.data as { success: boolean; message: string };

      if (data.success) {
        setState('success');
        toast.success(t('toasts.forgotPassword.emailSent'), {
          description: t('toasts.forgotPassword.checkInbox'),
        });
      } else {
        setState('error');
        setErrorMessage(data.message || t('toasts.forgotPassword.sendError'));
        toast.error(t('toasts.forgotPassword.sendError'), {
          description: data.message,
        });
      }
    } catch (error: any) {
      console.error('Error sending recovery email:', error);
      setState('error');

      // Parse Firebase error
      const errorMsg = error.message || t('toasts.forgotPassword.processError');
      setErrorMessage(errorMsg);

      toast.error(t('toasts.forgotPassword.sendError'), {
        description: errorMsg,
      });
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4">
      <div className="absolute top-4 right-4">
        <LanguageSwitcher />
      </div>

      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-center flex items-center justify-center gap-2">
            <Mail className="h-6 w-6 text-primary-600 dark:text-primary-400" />
            {t('toasts.forgotPassword.title')}
          </CardTitle>
          <p className="text-center text-ink-muted mt-2 text-sm">
            {t('toasts.forgotPassword.subtitle')}
          </p>
        </CardHeader>

        <CardContent>
          {state === 'success' ? (
            // Success State
            <div className="text-center space-y-4">
              <div className="flex justify-center">
                <div className="rounded-full bg-success/10 p-3">
                  <CheckCircle2 className="h-12 w-12 text-success" />
                </div>
              </div>

              <div>
                <h3 className="text-lg font-semibold text-ink mb-2">
                  {t('toasts.forgotPassword.emailSentTitle')}
                </h3>
                <p className="text-sm text-ink-muted mb-4">
                  {t('toasts.forgotPassword.emailSentDescription', { email })}
                </p>
                <p className="text-xs text-ink-subtle text-ink-muted">
                  {t('toasts.forgotPassword.checkSpam')}
                </p>
              </div>

              <div className="pt-4">
                <Link to="/login">
                  <Button className="w-full" variant="outline">
                    <ArrowLeft className="h-4 w-4 mr-2" />
                    {t('toasts.forgotPassword.backToLogin')}
                  </Button>
                </Link>
              </div>
            </div>
          ) : (
            // Form State
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label
                  htmlFor="email"
                  className="block text-sm font-medium text-ink-muted mb-2"
                >
                  {t('toasts.forgotPassword.emailLabel')}
                </label>
                <Input
                  id="email"
                  type="email"
                  placeholder={t('toasts.forgotPassword.emailPlaceholder')}
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  disabled={state === 'loading'}
                  className="w-full"
                  autoComplete="email"
                  required
                />
              </div>

              {state === 'error' && errorMessage && (
                <div className="bg-danger/10 border border-danger/30 rounded-lg p-3">
                  <p className="text-sm text-danger">{errorMessage}</p>
                </div>
              )}

              <Button type="submit" disabled={state === 'loading'} className="w-full">
                {state === 'loading' ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    {t('toasts.forgotPassword.sending')}
                  </>
                ) : (
                  <>
                    <Mail className="h-4 w-4 mr-2" />
                    {t('toasts.forgotPassword.sendRecoveryEmail')}
                  </>
                )}
              </Button>

              <div className="text-center pt-2">
                <Link
                  to="/login"
                  className="text-sm text-primary-600 dark:text-primary-400 hover:underline inline-flex items-center gap-1"
                >
                  <ArrowLeft className="h-3 w-3" />
                  {t('toasts.forgotPassword.backToLogin')}
                </Link>
              </div>
            </form>
          )}

          {/* Help Section */}
          <div className="mt-6 pt-6 border-t border-hairline">
            <p className="text-xs text-center text-ink-subtle">
              {t('toasts.forgotPassword.needHelp')}{' '}
              <a
                href="mailto:support@monkeybarber.club"
                className="text-primary-600 dark:text-primary-400 hover:underline"
              >
                {t('toasts.forgotPassword.contactUs')}
              </a>
            </p>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
